import React from 'react';
import { useSubscription, SubscriptionTier } from '../hooks/useSubscription';
import { Link } from 'react-router-dom';

interface FeatureGateProps {
  requiredTier: SubscriptionTier;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showUpgradePrompt?: boolean;
}

export const FeatureGate: React.FC<FeatureGateProps> = ({
  requiredTier,
  children,
  fallback,
  showUpgradePrompt = true
}) => {
  const {
    loading,
    error,
    canAccess,
    isSubscriptionActive,
    effectiveTier,
    refreshToken
  } = useSubscription();

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  if (error) {
    return (
      <div className="text-red-500">
        Error loading subscription status. Please try refreshing the page.
      </div>
    );
  }

  if (!isSubscriptionActive) {
    return (
      <div className="text-yellow-500">
        Your subscription is not active. Please check your subscription status.
      </div>
    );
  }

  if (canAccess(requiredTier)) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (!showUpgradePrompt) {
    return null;
  }

  const getUpgradeMessage = () => {
    switch (requiredTier) {
      case 'pro':
        return 'Upgrade to Pro';
      case 'basic':
        return effectiveTier === 'free' ? 'Upgrade to Basic' : 'Upgrade to Pro';
      default:
        return 'Upgrade';
    }
  };

  return (
    <div className="p-4 border rounded-lg bg-gray-50">
      <h3 className="text-lg font-semibold mb-2">Premium Feature</h3>
      <p className="text-gray-600 mb-4">
        This feature is available for {requiredTier} subscribers.
      </p>
      <Link
        to="/account"
        className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        onClick={refreshToken}
      >
        {getUpgradeMessage()}
      </Link>
    </div>
  );
}; 